import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import createNumberMask from 'text-mask-addons/dist/createNumberMask';
import { ToolBar } from '@1c/ui';
import {
  postTwoFactorPhoneAuthorizationConfirmAPI,
  postTwoFactorPhoneAuthorizationStartAPI,
} from 'src/serverApi/authorization';
import { postAcceptInvitationToOrganizationAPI } from 'src/serverApi/organizationUsers';
import { FinalField, FinalForm } from 'src/components/FinalForm';
import LinkButton from 'src/components/LinkButton';
import InputField from 'src/components/InputField';
import Button from 'src/components/Button';
import Text from 'src/components/Text';
import styles from './styles.module.scss';

type Props = {
  inviteOrganizationUeid?: string;
  inviteConfirmationId?: string | null | undefined;
  authorizationInfo?: {
    userId: string;
  };
  onBack: () => void;
  onSubmit: () => void;
};

const PhoneCodeForm: React.FC<Props> = ({
  inviteOrganizationUeid,
  inviteConfirmationId,
  authorizationInfo,
  onBack,
  onSubmit,
}) => {
  const inputMask = createNumberMask({
    integerLimit: 6,
    prefix: '',
    includeThousandsSeparator: false,
    allowLeadingZeroes: true,
  });

  const [isLoading, setIsLoading] = useState(false);
  const [confirmTimeout, setConfirmTimeout] = useState(0);
  const [attemptsRemained, setAttemptsRemained] = useState(0);

  const startConfirmTimer = (confirmTimeout: number) => {
    setConfirmTimeout(confirmTimeout);

    const interval = setInterval(() => {
      setConfirmTimeout(prev => {
        if (prev === 0) {
          clearInterval(interval);
          return prev;
        }

        return prev - 1;
      });
    }, 1 * 1000);
  };

  const twoFactorPhoneAuthorizationStart = async () => {
    try {
      setIsLoading(true);
      const sessionConfirmationState = await postTwoFactorPhoneAuthorizationStartAPI('SMS');
      setAttemptsRemained(sessionConfirmationState?.attemptsRemained);
      startConfirmTimer(sessionConfirmationState?.timeoutSeconds);
    } catch (error: any) {
      const message = error.type === 'PHONE_PROCESSING_EXCEPTION'
        ? 'Не удалось отправить СМС на указанный номер. Проверьте правильность введенного номера телефона или повторите попытку позже'
        : 'Произошла ошибка. Попробуйте позже.';
      toast.error(message);
      onBack();
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    twoFactorPhoneAuthorizationStart();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRepeatSMS = () => {
    twoFactorPhoneAuthorizationStart();
  };

  const confirmAuthorization = async (data: any) => {
    try {
      setIsLoading(true);
      const confirmData = await postTwoFactorPhoneAuthorizationConfirmAPI(data);

      if (confirmData?.status === 'SUCCESS') {
        // если авторизация происходит по ссылке приглашению, то нужно отправить запрос на подтверждение приглашения
        if (inviteOrganizationUeid && inviteConfirmationId) {
          try {
            await postAcceptInvitationToOrganizationAPI(
              inviteOrganizationUeid,
              inviteConfirmationId
            );
          } catch (error) {
            const { type } = error as any;
            const message =
              type === 'NOT_FOUND'
                ? 'Ссылка недействительна. Получите новую ссылку и перейдите по ней.'
                : 'Произошла ошибка подтверждения приглашения. Попробуйте позже.';
            toast.error(message);
          }
        }

        onSubmit();
      }

      // при ошибке кода подтверждения бэкенд присылает 200 ответ и в данных объект с status = 'IN_PROGRESS'
      // также уменьшается счетчик attemptsRemained
      // поэтому эта ошибка обрабатывается отдельно, не в блоке catch
      if (confirmData?.status === 'IN_PROGRESS') {
        if (confirmData?.attemptsRemained < attemptsRemained) {
          setAttemptsRemained(confirmData?.attemptsRemained);
          toast.error(
            `Некорректный код подтверждения. Осталось попыток: ${confirmData?.attemptsRemained}`
          );
        }
      }
    } catch (error) {
      const { type } = error as any;
      const messages: { [key: string]: string } = {
        ['CONCURRENT_EXECUTION']:
          'В другом сеансе сейчас работают с этим номером телефона. Необходимо подождать.',
        ['MAX_ATTEMPTS_EXCEEDED']:
          'Превышено количество попыток ввода кода. Необходимо дождаться завершения таймаута.',
        ['NOT_FOUND']: 'Не определен номер телефона, для которого отправлено подтверждение.',
        ['PHONE_PROCESSING_EXCEPTION']: 'неизвестная ошибка у оператора SMS.',
      };
      toast.error(messages[type] || 'Произошла ошибка. Попробуйте позже.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = (values: any) => {
    confirmAuthorization(values?.code);
  };

  return (
    <>
      <Text type="p" muted className={styles['text']}>
        В ближайшее время на номер {authorizationInfo?.userId} придет СМС с кодом.
      </Text>
      <FinalForm
        onSubmit={handleSubmit}
        submitting={isLoading}
        pristine
        initialValues={{ code: undefined }}
      >
        {({ submitting, pristine }: { submitting: any; pristine: any }) => {
          return (
            <div className={styles['form']}>
              <FinalField name="code">
                {({ onChange }: { onChange: any }) => (
                  <InputField label="Введите код" onChange={onChange} mask={inputMask} />
                )}
              </FinalField>
              <div className={styles['resend-wrapper']}>
                <Text type="span" small muted>
                  Не пришло СМС?
                </Text>
                <br />
                <LinkButton
                  disabled={confirmTimeout > 0}
                  onClick={handleRepeatSMS}
                  className={styles['link']}
                >
                  Отправить повторно
                </LinkButton>
                {confirmTimeout > 0 && (
                  <Text type="span" small muted>
                    {' '}
                    (через {confirmTimeout} сек.)
                  </Text>
                )}
              </div>
              <ToolBar align="center" className={styles['buttons']}>
                <Button variant="text" disabled={submitting} onClick={onBack}>
                  Назад
                </Button>
                <Button disabled={submitting || pristine} type="submit">
                  Продолжить
                </Button>
              </ToolBar>
            </div>
          );
        }}
      </FinalForm>
    </>
  );
};

export default PhoneCodeForm;
